import { ENUM_LABELS, type ReagentItemStock } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { formatBaseQuantity } from '#client/lib/format';
import {
  type DraftRequisitionLine,
  type RequisitionFields,
  reagentOptionsFor,
} from './requisition.logic';
import type { SetField } from '#client/hooks/useFormValues';
import type { Repeater } from '#client/hooks/useRepeater';

type Option = { value: string; label: string };

interface RequisitionHeaderCardProps {
  values: RequisitionFields;
  onFieldChange: SetField<RequisitionFields>;
  errors: Record<string, string>;
  supplierOptions: Option[];
  departmentOptions: Option[];
}

export function RequisitionHeaderCard({
  values,
  onFieldChange,
  errors,
  supplierOptions,
  departmentOptions,
}: RequisitionHeaderCardProps) {
  return (
    <Card title="Who and when">
      <div className="c-form-grid">
        <Select
          label="Supplier"
          options={supplierOptions}
          placeholder="Not chosen yet"
          value={values.supplier_id === null ? '' : String(values.supplier_id)}
          error={errors.supplier_id}
          onChange={(choice) => onFieldChange('supplier_id', choice ? Number(choice) : null)}
          helpText="Can be left until the order is approved"
        />
        <Select
          label="Bench"
          options={departmentOptions}
          placeholder="The whole lab"
          value={values.department_id === null ? '' : String(values.department_id)}
          error={errors.department_id}
          onChange={(choice) => onFieldChange('department_id', choice ? Number(choice) : null)}
        />
        <TextField
          label="Expected"
          type="date"
          value={values.expected_at ?? ''}
          error={errors.expected_at}
          onChange={(event) => onFieldChange('expected_at', event.target.value)}
          helpText="What the supplier promised. A promise, not a fact."
        />
      </div>

      <TextArea
        label="Note"
        value={values.note ?? ''}
        error={errors.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
        rows={2}
      />
    </Card>
  );
}

interface RequisitionLinesCardProps {
  lines: Repeater<DraftRequisitionLine>;
  reagents: readonly ReagentItemStock[];
  linesError: string | undefined;
  isSuggesting: boolean;
  onFillFromLowStock: () => void;
}

export function RequisitionLinesCard({
  lines,
  reagents,
  linesError,
  isSuggesting,
  onFillFromLowStock,
}: RequisitionLinesCardProps) {
  const chosenItemIds = new Set(lines.rows.map((line) => line.item_id));

  return (
    <Card
      title="What to order"
      subtitle="Whole packs — no supplier has an opinion about microlitres"
      actions={
        <div className="c-card__actions">
          <Button size="sm" onClick={onFillFromLowStock} loading={isSuggesting}>
            Fill from the reorder list
          </Button>
          <Button size="sm" onClick={lines.add}>
            Add a reagent
          </Button>
        </div>
      }
    >
      {linesError && <p className="c-import-missing">{linesError}</p>}

      {lines.rows.length === 0 ? (
        <p className="c-field__help">
          Nothing on this order yet. Add a reagent, or fill it from everything that is at or below
          its reorder level.
        </p>
      ) : (
        <ul className="c-order-lines">
          {lines.rows.map((line, index) => (
            <RequisitionLineRow
              key={lines.keys[index]}
              line={line}
              reagent={reagents.find((candidate) => candidate.id === line.item_id)}
              reagentOptions={reagentOptionsFor(reagents, line.item_id, chosenItemIds)}
              onChange={(changes) => lines.update(index, changes)}
              onRemove={() => lines.remove(index)}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

interface RequisitionLineRowProps {
  line: DraftRequisitionLine;
  reagent: ReagentItemStock | undefined;
  reagentOptions: Option[];
  onChange: (changes: Partial<DraftRequisitionLine>) => void;
  onRemove: () => void;
}

function RequisitionLineRow({
  line,
  reagent,
  reagentOptions,
  onChange,
  onRemove,
}: RequisitionLineRowProps) {
  return (
    <li className="c-order-line">
      <Select
        label="Reagent"
        options={reagentOptions}
        placeholder="Choose a reagent"
        value={line.item_id === 0 ? '' : String(line.item_id)}
        onChange={(choice) => onChange({ item_id: Number(choice) })}
        required
      />
      <NumberField
        label="Packs"
        min={1}
        value={line.quantity_packs}
        onChange={(packs) => onChange({ quantity_packs: packs })}
        suffix={reagent ? reagent.pack_label : undefined}
        required
      />
      <NumberField
        label="Price per pack"
        min={0}
        step={0.01}
        nullable
        value={line.unit_price}
        onChange={(price) => onChange({ unit_price: price })}
        prefix="Rs"
        helpText="If the supplier has quoted"
      />
      <Button variant="ghost" size="sm" onClick={onRemove}>
        Remove
      </Button>
      {reagent && (
        <p className="c-order-line__stock">
          {formatBaseQuantity(reagent.on_hand_base)} {ENUM_LABELS.stockUnit[reagent.base_unit]} on
          hand · reorder at {formatBaseQuantity(reagent.reorder_level_base)}
        </p>
      )}
    </li>
  );
}
